import { Box, SxProps, Typography } from '@mui/material';
import { StepContent } from 'components/Stepper/StepContent';
import { useStepper } from 'hooks/useStepper';
import {
  whatsAppOnboardingStepMapper,
  WhatsAppOnboardingSteps
} from '../constants';
import { MetaBusinessAccountStep } from './MetaBusinessAccountStep';
import { SetupWhatsAppBusinessAccountStep } from './SetupWhatsAppBusinessAccountStep';
import { useMemo } from 'react';
import { WhatsAppPhoneNumberStep } from './WhatsAppPhoneNumberStep';
import { useFormik } from 'formik';
import Yup from 'utils/validation';
import { MetaLoginStep } from './MetaLoginStep';
import { useFacebookInit } from 'core/services/facebook/hooks/useFacebookInit';
import { UserWithNoPhoneNumberStep } from './UserWithNoPhoneNumberStep';
import { organizationAtom } from 'services/organization/atom';
import { useRecoilValue } from 'recoil';

const container: SxProps = {
  mr: 4,
  width: 400
};

const validationSchema = Yup.object().shape({
  phoneNumber: Yup.string().phoneNumber(
    'Enter a valid phone number, including the country code'
  )
});

export const WhatsAppSetupDrawer = () => {
  useFacebookInit();

  const organization = useRecoilValue(organizationAtom);
  const isPhoneNumberSet = organization?.twilioPhoneNumber;

  const { activeStep, handleNext, handleBack } = useStepper(
    isPhoneNumberSet
      ? whatsAppOnboardingStepMapper[WhatsAppOnboardingSteps.LOGIN_TO_META]
      : whatsAppOnboardingStepMapper[
          WhatsAppOnboardingSteps.META_BUSINESS_ACCOUNT
        ]
  );

  const formik = useFormik({
    initialValues: {
      phoneNumber: ''
    },
    onSubmit: () => {
      //
    },
    validationSchema
  });

  const title = useMemo(() => {
    if (
      activeStep ===
      whatsAppOnboardingStepMapper[
        WhatsAppOnboardingSteps.SETUP_META_BUSINESS_ACCOUNT
      ]
    ) {
      return 'Set up a Meta Business Account to use WhatsApp';
    }

    if (
      activeStep ===
      whatsAppOnboardingStepMapper[
        WhatsAppOnboardingSteps.USER_WITH_NO_PHONE_NUMBER
      ]
    ) {
      return "If you don't have a number";
    }

    return 'WhatsApp Setup';
  }, [activeStep]);

  return (
    <Box sx={container}>
      <Typography variant="h5" fontWeight="400">
        {title}
      </Typography>

      <Box mt={2}>
        <StepContent
          activeStepIndex={activeStep}
          stepIndex={
            whatsAppOnboardingStepMapper[
              WhatsAppOnboardingSteps.META_BUSINESS_ACCOUNT
            ]
          }
        >
          <MetaBusinessAccountStep handleNext={handleNext} />
        </StepContent>

        <StepContent
          activeStepIndex={activeStep}
          stepIndex={
            whatsAppOnboardingStepMapper[
              WhatsAppOnboardingSteps.SETUP_META_BUSINESS_ACCOUNT
            ]
          }
        >
          <SetupWhatsAppBusinessAccountStep
            handleNext={handleNext}
            handleBack={handleBack}
          />
        </StepContent>

        <StepContent
          activeStepIndex={activeStep}
          stepIndex={
            whatsAppOnboardingStepMapper[WhatsAppOnboardingSteps.PHONE_NUMBER]
          }
        >
          <WhatsAppPhoneNumberStep
            handleBack={handleBack}
            handleNext={handleNext}
            formik={formik}
          />
        </StepContent>

        <StepContent
          stepIndex={
            whatsAppOnboardingStepMapper[WhatsAppOnboardingSteps.LOGIN_TO_META]
          }
          activeStepIndex={activeStep}
        >
          <MetaLoginStep
            handleNext={handleNext}
            handleBack={handleBack}
            formik={formik}
          />
        </StepContent>

        <StepContent
          stepIndex={
            whatsAppOnboardingStepMapper[
              WhatsAppOnboardingSteps.USER_WITH_NO_PHONE_NUMBER
            ]
          }
          activeStepIndex={activeStep}
        >
          <UserWithNoPhoneNumberStep handleNext={handleNext} />
        </StepContent>
      </Box>
    </Box>
  );
};
